import { IAttribute, IModel, IValidationContext, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { LinkedService } from './linkedservice';
import { shellStringResources as stringResources } from '../../Resources/ShellStringResources';
import { AzureKeyVault } from './key-vault';
import { format } from '../../shared/utils/string.util';
import { IPrivateEndpointCompatible } from '../../interfaces/IPrivateEndpointCompatible';
import { IAzureBlobLinkedServiceValidators } from './azure-blob-fs.linkedservice';
import { AuthenticationType, azureStorageAuthTypes } from '../../interfaces/IAuthenticationType';
import { ModelConstants } from '../model-constants';
import { Credential } from '../credential/credential';

export const fileStorageLinkedServiceValidators: IAzureBlobLinkedServiceValidators = {
    validateAKVLinkedserviceReference: undefined
};

const validator = (linkedservice: LinkedService, referencedResource: AzureKeyVault, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
    if (fileStorageLinkedServiceValidators.validateAKVLinkedserviceReference) {
        return fileStorageLinkedServiceValidators.validateAKVLinkedserviceReference(linkedservice, referencedResource, violation, context);
    }
    return undefined;
};

/*
    some of code is copy from AzureStorageLinkedService
    (Product/projects/shell-models-lib/src/lib/model/linkedservice/azure-storage.linkedservice.ts)
    There is a reason not to inherit AzureStorageLinkedService, but I forget.
 */

@Metamodel.model({
    name: ModelConstants.fileStorageService,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureFileStorage_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.AzureFileStorage_DisplayText)
    ]
})
export class AzureFileStorageLinkedService extends LinkedService implements IPrivateEndpointCompatible {

    public static readonly MODEL: IModel;

    public static readonly HOST: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly FILE_SHARE: IAttribute;

    // the same as AzureStorageLinkedService
    public static readonly ACCOUNT_NAME: IAttribute;
    public static readonly ACCOUNT_KEY: IAttribute;
    public static readonly ENDPOINT_SUFFIX: IAttribute;
    public static readonly SAS_URI: IAttribute;
    public static readonly SAS_TOKEN: IAttribute;
    public static readonly PRIMARY_ENDPOINT: IAttribute;
    public static readonly ENABLE_PARTITIONED_DNS: IAttribute;
    public static readonly SERVICE_ENDPOINT: IAttribute;

    public authenticationMethod?: string = azureStorageAuthTypes.AccountKeyAuthentication;

    @Metamodel.attribute({
        display: stringResources.ModelName_PrimaryEndpoint,
        association: 'PRIMARY_ENDPOINT',
        templatable: true
    })
    public primaryEndpoint: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_StorageAccountName,
        association: 'ACCOUNT_NAME',
        templatable: true
    })
    public accountName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_StorageAccountKey,
        association: 'ACCOUNT_KEY',
        templatable: true,
        accepts: ['String'],
        constraint: {
            validator
        }
    })
    public accountKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_EndpointSuffix,
        association: 'ENDPOINT_SUFFIX',
        templatable: true
    })
    public endpointSuffix?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_SasURI,
        association: 'SAS_URI',
        templatable: true
    })
    public sasUri?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServiceEndpoint,
        association: 'SERVICE_ENDPOINT',
        templatable: true,
    })
    public serviceEndpoint?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_SasURI,
        constraint: {
            validator
        }
    })
    public referedSasUriAKV?: AzureKeyVault;

    public enableSasUriAKV?: boolean = false;

    @Metamodel.attribute({
        display: stringResources.ModelName_SasToken,
        association: 'SAS_TOKEN',
        templatable: true,
        accepts: ['String'],
        constraint: {
            validator
        }
    })
    public sasToken?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_Host,
        association: 'HOST',
        templatable: true,
        constraint: {
            validator: AzureFileStorageLinkedService.validateHost
        }
    })
    public host?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public username?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'PASSWORD',
        templatable: true,
        constraint: {
            validator
        }
    })
    public password?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.FileShare_DisplayText,
        association: 'FILE_SHARE',
        templatable: true
    })
    public fileShare?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Snapshot
    })
    public snapshot?: string;

    @Metamodel.attribute({
        display: stringResources.Credential,
        reference: true,
        constraint: {
            validator: (
                linkedservice: AzureFileStorageLinkedService,
                referencedCredential: Credential
            ): IViolation[] | undefined => {
                if (
                    linkedservice?.authenticationMethod ===
                    azureStorageAuthTypes.UserAssignedManagedIdentity &&
                    !referencedCredential
                ) {
                    return [
                        {
                            message: () =>
                                stringResources.Error_InvalidCredentialReferenced,
                        },
                    ];
                }
                return undefined;
            },
        }
    })
    public credential?: Credential;

    public enableAccountAKV?: boolean = false;

    @Metamodel.attribute({
        display: stringResources.ModelName_StorageAccountKey,
        constraint: {
            validator
        }
    })
    public referedAccountAKV?: AzureKeyVault;

    @Metamodel.attribute({
        templatable: true,
        association: 'ENABLE_PARTITIONED_DNS',
        display: stringResources.enablePartitionedDns
    })
    public isPartitionedDns?: boolean;

    private _accountResourceID: string;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public static validateHost(fileserver: AzureFileStorageLinkedService): IViolation[] | undefined {
        if (fileserver && fileserver.host) {
            // eslint-disable-next-line
            if (fileserver.host.toLowerCase().startsWith('http:')) {
                return [{
                    context: fileserver,
                    message: () => stringResources.FileSystem_HostStartWithHttp_ErrorMessage
                }];
            }
            if (fileserver.host.toLowerCase().startsWith('ftp:')) {
                return [{
                    context: fileserver,
                    message: () => stringResources.FileSystem_HostStartWithFtp_ErrorMessage
                }];
            }
        }
        return undefined;
    }

    public getPEResourceIDAndName(): { id: string, name: string } {
        const resourceName = this._getPEResourceName();
        if (!resourceName || this._accountResourceID && !this._accountResourceID.endsWith('/' + resourceName)) {
            this._accountResourceID = undefined;
        }
        return {
            id: this._accountResourceID,
            name: resourceName
        };
    }

    public setPEResourceID(id: string): void {
        this._accountResourceID = id;
    }

    protected _getPEResourceName(): string {
        switch (this.authenticationMethod) {
            case azureStorageAuthTypes.AccountKeyAuthentication:
                return this.accountName || '';
            case azureStorageAuthTypes.SasUriAuthentication:
                if (this.sasUri?.toLowerCase().startsWith('https://')) {
                    return this.sasUri.substr(8).split('.')[0];
                }
                return '';
            case azureStorageAuthTypes.MSIAuthentication:
            case azureStorageAuthTypes.UserAssignedManagedIdentity:
                if (this.serviceEndpoint?.toLowerCase().startsWith('https://')) {
                    return this.serviceEndpoint.substr(8).split('.')[0];
                }
                return '';
            case AuthenticationType.Basic:
                if (this.username?.toLowerCase().startsWith('azure\\')) {
                    return this.username.substr(6); // Azure\<storagename>
                }
                return '';
            default:
                return '';
        }
    }
}
